import { IApiAxiosError } from "@/api";
import { createPolicyType, IPolicyTypeCreate } from "@/api/policy-types";
import { Modal } from "@/components/shared/Modal";
import { TextField } from "@/components/shared/TextField";
import { Stack, Typography } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { DialogProps } from "@toolpad/core/useDialogs";
import React from "react";

export const NewPolicyTypeDialog = ({ open, onClose }: DialogProps) => {
    const [name, setName] = React.useState("");
    const [shortName, setShortName] = React.useState("");
    const queryClient = useQueryClient();

    const createMutation = useMutation({
        mutationFn: (data: IPolicyTypeCreate) => createPolicyType(data),
        onSuccess: () => {
            handleClose();
            queryClient.invalidateQueries({
                queryKey: ["admin-policy-types"],
            });
        },
    });

    const handleCreate = () => {
        if (!name.trim() || !shortName.trim()) return;

        createMutation.mutate({
            name: name.trim(),
            short_name: shortName.trim(),
        });
    };

    const handleClose = () => {
        onClose();
        setName("");
        setShortName("");
        createMutation.reset();
    };

    const axiosError = createMutation.error as IApiAxiosError;

    return (
        <Modal
            open={open}
            onClose={handleClose}
            title="Create a New Policy Type"
            isLoading={createMutation.isPending}
            actions={[
                {
                    label: "Create",
                    onClick: handleCreate,
                    variant: "contained",
                    color: "primary",
                    disabled:
                        !name.trim() ||
                        !shortName.trim() ||
                        createMutation.isPending,
                },
            ]}
            footerText={
                axiosError && (
                    <Typography
                        variant="caption"
                        color="error"
                        sx={{ fontSize: "0.75rem" }}
                    >
                        {axiosError.response?.data.detail ||
                            "An error occurred while creating the policy type."}
                    </Typography>
                )
            }
        >
            <Stack spacing={2}>
                <TextField
                    label="Name"
                    error={!!axiosError}
                    value={name}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setName(e.target.value)
                    }
                    inputProps={{ autoFocus: true }}
                    required
                    sx={{ width: "100%" }}
                />
                <TextField
                    label="Short Name"
                    error={!!axiosError}
                    value={shortName}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setShortName(e.target.value)
                    }
                    onKeyPress={(e: React.KeyboardEvent) => {
                        if (e.key === "Enter") {
                            handleCreate();
                        }
                    }}
                    required
                    sx={{ width: "100%" }}
                />
            </Stack>
        </Modal>
    );
};
